/**
 * t241u: "Remind me" on a task or subtask = a personal reminder on that task (only you see it), not another private
 * task. Quick picks (Later today 17:00, Tomorrow 09:00, Next week Mon 09:00) or a custom London date and time.
 */
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { AlarmClock, X } from 'lucide-react';
import { reminderChoices, reminderFromLocal, reminderLabel, reminderWhen, REMINDER_TZ } from '@tradeoutlet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { useUi } from '@/lib/store';
import { Button, Input } from '@/components/ui';

export const reminderKey = (taskId: string) => ['task-reminder', taskId] as const;

export function useTaskReminder(taskId: string) {
  const qc = useQueryClient();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: reminderKey(taskId), queryFn: () => api.taskReminder(taskId), staleTime: 30_000 });
  const set = useMutation({
    mutationFn: (at: string) => api.setTaskReminder(taskId, at),
    onSuccess: r => { qc.setQueryData(reminderKey(taskId), { reminder: r.reminder }); toast(`We'll remind you ${reminderWhen(r.reminder.remind_at).replace(/^(Today|Tomorrow)/, m => m.toLowerCase())}`, 'success'); },
    onError: e => toast(errorMessage(e, 'Couldn’t set the reminder'), 'error'),
  });
  const clear = useMutation({
    mutationFn: () => api.clearTaskReminder(taskId),
    onSuccess: () => { qc.setQueryData(reminderKey(taskId), { reminder: null }); toast('Reminder removed', 'success'); },
    onError: e => toast(errorMessage(e), 'error'),
  });
  return { reminder: q.data?.reminder ?? null, set, clear };
}

/** The picker body: quick picks, Custom date and time, and Remove when one is set. */
export function ReminderOptions({ taskId, onDone, phone }: { taskId: string; onDone: () => void; phone?: boolean }) {
  const { reminder, set, clear } = useTaskReminder(taskId);
  const choices = reminderChoices();
  const tomorrow = DateTime.now().setZone(REMINDER_TZ).plus({ days: 1 }).toISODate()!;
  const [custom, setCustom] = useState({ date: tomorrow, time: '09:00' });
  const customAt = reminderFromLocal(custom.date, custom.time);
  const row = clsx('flex w-full items-center gap-3 text-left', phone ? 'min-h-[52px] px-4 text-base active:bg-slate-100 dark:active:bg-slate-800' : 'rounded-md px-2 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-700');
  const pick = (at: string) => set.mutate(at, { onSuccess: onDone });
  return (
    <div data-testid="reminder-options">
      {choices.map(c => (
        <button key={c.id} type="button" className={row} onClick={() => pick(c.at)} disabled={set.isPending} data-testid={`reminder-${c.id}`}>
          <AlarmClock className="h-4 w-4 shrink-0 text-slate-500" aria-hidden /><span className="flex-1">{c.label}</span>
          <span className="text-xs text-slate-500">{reminderWhen(c.at).replace(/^(Today|Tomorrow) /, '')}</span>
        </button>
      ))}
      <div className={clsx('flex items-center gap-2', phone ? 'px-4 py-2' : 'px-2 py-1.5')} data-testid="reminder-custom">
        <Input type="date" aria-label="Reminder date" className={clsx(phone ? 'h-11' : 'h-8', 'min-w-0 flex-1')} value={custom.date} min={DateTime.now().setZone(REMINDER_TZ).toISODate()!} onChange={e => setCustom({ ...custom, date: e.target.value })} />
        <Input type="time" aria-label="Reminder time" className={clsx(phone ? 'h-11 w-[6.25rem]' : 'h-8 w-[7.5rem]', 'shrink-0')} value={custom.time} onChange={e => setCustom({ ...custom, time: e.target.value })} />
        <Button size={phone ? 'md' : 'sm'} className={clsx(phone && '!h-11 shrink-0')} disabled={!customAt || Date.parse(customAt) <= Date.now() || set.isPending} onClick={() => customAt && pick(customAt)} data-testid="reminder-custom-set">Set</Button>
      </div>
      {reminder && (
        <button type="button" className={clsx(row, 'text-rose-700 dark:text-rose-300')} onClick={() => clear.mutate(undefined, { onSuccess: onDone })} data-testid="reminder-remove">
          <X className="h-4 w-4 shrink-0" aria-hidden />Remove reminder
        </button>
      )}
    </div>
  );
}

function usePop() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', down); document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [open]);
  return { open, setOpen, ref };
}

/** Desktop task panel header: the alarm clock opens the picker. */
export function TaskReminderButton({ taskId }: { taskId: string }) {
  const { open, setOpen, ref } = usePop();
  const { reminder } = useTaskReminder(taskId);
  return (
    <div ref={ref} className="relative">
      <Button size="icon" variant="ghost" title={reminder ? reminderLabel(reminder) : 'Remind me'} aria-expanded={open} onClick={() => setOpen(!open)} className={clsx(reminder && 'bg-accent text-on-accent hover:bg-accent')} data-testid="task-remind-me">
        <AlarmClock className="h-4 w-4" />
      </Button>
      {open && (
        <div role="dialog" aria-label="Remind me" data-testid="reminder-picker" className="on-page absolute right-0 z-40 mt-1 w-96 rounded-lg bg-white p-1.5 shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
          <p className="px-2 pb-1 pt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Remind me</p>
          <ReminderOptions taskId={taskId} onDone={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

/** Desktop task panel body: "Reminder Tue 09:00" with Change and ×. Only the person who set it sees it. */
export function TaskReminderChip({ taskId }: { taskId: string }) {
  const { open, setOpen, ref } = usePop();
  const { reminder, clear } = useTaskReminder(taskId);
  if (!reminder) return null;
  return (
    <div ref={ref} className="relative mb-2 inline-flex" data-testid="task-reminder">
      <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-accent pl-2.5 pr-1 text-xs font-semibold text-on-accent">
        <AlarmClock className="h-3.5 w-3.5" aria-hidden />{reminderLabel(reminder)}
        <button type="button" className="rounded-full px-1.5 underline-offset-2 hover:underline" onClick={() => setOpen(!open)} data-testid="task-reminder-change">Change</button>
        <button type="button" aria-label="Remove reminder" className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-black/10" onClick={() => clear.mutate()} data-testid="task-reminder-remove"><X className="h-3 w-3" /></button>
      </span>
      {open && (
        <div role="dialog" aria-label="Change reminder" className="on-page absolute left-0 top-8 z-40 w-96 rounded-lg bg-white p-1.5 shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
          <ReminderOptions taskId={taskId} onDone={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}
